// Catalog.ID Client — Registry Ceremony Envelope Types
// ============================================================================
// Wire shapes for the keybag-hop ceremony modes: the four `registry-request`
// modes through which the CPR module asks this keybag to custody and use an
// ACCOUNT AUTHORITY key (asset-ownership plan §8, A8b).
//
// Why these ride the existing `registry-request` family rather than a new one:
// the relay is content-agnostic (KeyGrantRelay's own doc calls a new family a
// "one-line extension", but a new MODE is a zero-line one), the requester is
// already pinned to `cpr` by `dispatchRegistryRequest`, and the sender
// verification, clock-skew window and peer-directory gates all apply unchanged.
//
// The four modes, and why each exists separately:
//
//   ceremony-authority-status
//     Read-only. "Which registry authority keys does this keybag hold, and
//     which account streams is this identity enrolled in?" CPR needs the
//     answer to decide whether to offer the keybag path at all, and to pin
//     `expectedAuthorityFp` on the request that follows. Public-key material
//     only, so no consent — same posture as `get-active-identity`.
//
//   ceremony-generate-authority-key
//     Claims the next GENERATION of the account's hybrid Ed25519+ML-DSA-65
//     authority key and returns only its public half. The key itself is
//     derived from the account's derive root (cid-encrypted-storage §5.2), so
//     what this mode really does is record the generation in the
//     `cid/registry-authority` blob. Separate from signing because the
//     generation must be recorded BEFORE the claim naming its key is built: an
//     `authority_rotate` body carries the incoming public key in its signed
//     bytes, so it cannot be constructed until this returns. No supersecret
//     and no dialog — there is no bag mutation left to gate, and a key that is
//     generated but never named in a claim does nothing at all.
//
//   sign-ceremony-claims
//     The signing mode. Claim-type whitelist first, then ONE structured
//     consent for the whole chain, then signatures. See ceremony-claims.ts for
//     why the whitelist is inviolate.
//
//   ceremony-commit
//     Bookkeeping AFTER the operator accepted the chain: records the account
//     stream this identity is enrolled in as an off-ledger CID claim, so a
//     second machine can discover it. Cannot be folded into signing — at
//     signing time nothing has been accepted yet.
//
// Every field here is optional on the shared `RegistryRequest`/`RegistryResponse`
// interfaces (registry-types.ts extends these), exactly as the
// register-registry-key fields are: one envelope shape, mode-discriminated
// contents. Drift with the C# side breaks the relay's canonical-bytes
// verification (`KeyGrantRelay.TryBuildCanonicalForInboundMode`), never
// silently.
// ============================================================================

/** The four ceremony modes. `sign-ceremony-claims` is the pinned name. */
export type CeremonyRequestMode =
    | 'ceremony-authority-status'
    | 'ceremony-generate-authority-key'
    | 'sign-ceremony-claims'
    | 'ceremony-commit';

/**
 * WHOSE keybag and authority signs. v1 has exactly one value.
 *
 * The field exists — rather than an implicit "whoever is logged in" — so that
 * acting-as-org (org keybag custody + admin-role gating, work's migration plan
 * §13 is the forcing consumer) is added later as a VALUE, not as a rework of
 * every caller. A request naming any other kind is refused; silently treating
 * an unknown context as personal is how an org ceremony would one day get
 * signed by a personal key.
 */
export const CEREMONY_IDENTITY_KIND_PERSONAL = 'personal';

/** Ceremony flavours, used for the consent heading and the commit record. */
export type CeremonyKind =
    | 'enrollment'
    | 'rotation'
    | 'successor'
    | 'grant'
    | 'revoke'
    | 'rebind';

/** One claim the requester wants signed, as it travels on the wire. */
export interface CeremonyClaimPlanWire {
    /** 128-hex fingerprint of the keybag key that must sign the outer claim. */
    signerFingerprint: string;
    /**
     * `authority_rotate` only: the INCOMING key that signs `new_authority_proof`
     * over the body-minus-proof (Q-A4-1). The outer signer stays the CURRENT
     * authority (or the declared successor, D8b); the proof is always by the
     * key being installed.
     */
    proofSignerFingerprint?: string;
    /** The A1 §5.8 claim body. No `claim_id`; no `new_authority_proof`. */
    body: Record<string, unknown>;
}

/** One signed claim, as it travels back. */
export interface CeremonySignedClaimWire {
    /**
     * The FINAL claim body — byte-identical to the submitted `body` except on
     * an `authority_rotate`, which gains the `new_authority_proof` the keybag
     * computed. The requester submits exactly this object; re-deriving it
     * would be a second source of truth for bytes a signature already covers.
     */
    claim: Record<string, unknown>;
    /** Base64 of the JCS canonical bytes actually signed (preview / fee estimate). */
    canonicalBytes: string;
    /** 128-hex SHA-512 of those bytes. */
    claimDigest: string;
    signerFingerprint: string;
    /** Base64 of the 1984-byte combined hybrid public key — the envelope proof key. */
    publicKey: string;
    /** Base64 of the 3373-byte combined signature `ed25519(64) || mldsa65(3309)`. */
    signature: string;
}

/** One registry authority key this identity custodies. */
export interface CeremonyAuthorityKeyWire {
    /** 128-hex SHA-512 of the combined public key. */
    fingerprint: string;
    /** Base64 combined hybrid public key (1984 bytes). */
    publicKey: string;
    /** Generation index; the highest is the most recently minted. Since
     *  cid-encrypted-storage M5 it is the index in `cid/registry-authority`
     *  and the KDF info the key derives under — it was the keybag `keyIndex`
     *  before, and the field name and meaning are unchanged for CPR. */
    keyIndex: number;
}

/** One account stream this identity has recorded an enrollment for. */
export interface CeremonyEnrolledStreamWire {
    streamGenesisId: string;
    operatorId: string;
    /** 32-hex key handle — the stream's subject, forever the GENESIS key's handle. */
    handle: string;
    /** The authority fingerprint AS OF the last commit for this stream. */
    authorityFingerprint: string;
    updatedAt: string;
}

/** Mode-specific REQUEST fields, mixed into `RegistryRequest`. */
export interface CeremonyRequestFields {
    /** All four modes. `{ kind: 'personal' }` in v1. */
    identityContext?: { kind: string };

    /**
     * `sign-ceremony-claims`: the fingerprint the requester believes it is
     * asking to sign with. The keybag refuses when it holds no such key, so a
     * machine pointed at the wrong Catalog.ID account fails here rather than
     * producing a valid signature by the wrong authority.
     */
    expectedAuthorityFp?: string;

    /** `sign-ceremony-claims` / `ceremony-commit`: see {@link CeremonyKind}. */
    ceremonyKind?: string;
    /** `sign-ceremony-claims`: a one-line human label for the consent heading. */
    ceremonyLabel?: string;

    /**
     * `sign-ceremony-claims`: base64 SHA-512 over the JCS canonical form of the
     * whole ordered chain. Committed in the relay-verified canonical bytes; the
     * browser recomputes it from `claims` and refuses on mismatch, which is
     * what stops a verified envelope being paired with a substituted body.
     */
    claimsDigestBase64?: string;
    /** `sign-ceremony-claims`: the ordered chain itself. */
    claims?: CeremonyClaimPlanWire[];

    /**
     * `ceremony-generate-authority-key`: a display label for the new key. It is
     * accepted and IGNORED since cid-encrypted-storage M5 — there is no bag
     * entry left to label — but it stays on the envelope because it is part of
     * the relay-signed canonical bytes (`CrossModuleCanonical
     * .BuildCeremonyGenerateAuthorityKey`).
     */
    keyLabel?: string;

    /** `ceremony-commit`: the accepted stream's genesis claim id. */
    streamGenesisId?: string;
    /** `ceremony-commit`: the operator that accepted it. */
    streamOperatorId?: string;
    /** `ceremony-commit`: the stream handle (32 hex). */
    streamHandle?: string;
    /** `ceremony-commit`: the authority fingerprint now in force for the stream. */
    authorityFingerprint?: string;
}

/** Mode-specific RESPONSE fields, mixed into `RegistryResponse`. */
export interface CeremonyResponseFields {
    /** `ceremony-authority-status`: every registry authority key in the bag. */
    authorityKeys?: CeremonyAuthorityKeyWire[];
    /**
     * `ceremony-authority-status`: the generation the document marks `active`,
     * or null when the identity has none. Advisory: the CPR vault's own
     * enrollment record is what pins `expectedAuthorityFp`, because a
     * generated-but-unused key would otherwise read as "current" the moment a
     * ceremony was abandoned half way.
     */
    activeAuthorityFingerprint?: string | null;
    /** `ceremony-authority-status`: enrollments recorded by `ceremony-commit`. */
    enrolledStreams?: CeremonyEnrolledStreamWire[];

    /** `ceremony-generate-authority-key`: the new key's public half. */
    generatedFingerprint?: string;
    generatedPublicKey?: string;
    generatedKeyIndex?: number;

    /** `sign-ceremony-claims`: one entry per requested claim, in order. */
    signedClaims?: CeremonySignedClaimWire[];

    /** `ceremony-commit`: the CID claim uuid holding the enrollment record. */
    enrollmentClaimUuid?: string;
}
